import {
  IconAlertCircle,
  IconAlertTriangle,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";

export type BannerTone = "neutral" | "info" | "warning" | "danger";

interface PBannerProps {
  tone: BannerTone;
  /**
   * "strip" — full-bleed row directly under the app bar (separated by a
   * `border-b`, the house grammar for chrome that sits against the bar).
   * "card" — boxed callout inside a padded content column.
   */
  shape?: "strip" | "card";
  /** Loud filled slab. One caller only: the blocking tenant-restricted banner. */
  emphasis?: "quiet" | "solid";
  title: string;
  description?: string;
  /**
   * Keep the description on narrow containers too. Default off: a phone gets
   * the headline alone. For a CARD whose description is the reason the reader
   * needs (the SMS page's "blocked" / "paused" / "switched off" states — what
   * happened, what is kept), not a gloss on the headline.
   */
  descriptionOnNarrow?: boolean;
  onDismiss?: () => void;
  actionHref?: string;
  actionLabel?: string;
  /**
   * Arbitrary action node; wins over actionHref/actionLabel. Note the
   * `@container` on the root makes this box a containing block for
   * `position: fixed` descendants — do not hand it a fixed-positioned popover.
   */
  actionSlot?: React.ReactNode;
  /**
   * Replaces the tone's leading icon — e.g. an info icon whose hover says
   * why, so the row carries ONE info icon, not the tone's plus a hover's.
   */
  iconSlot?: React.ReactNode;
  testId?: string;
  /** Escalation rung, surfaced as `data-rung` for tests and e2e. */
  rung?: string;
}

/**
 * CARD tint + border per tone. A card is a callout INSIDE a content column —
 * it has to separate itself from the page, so it keeps the tinted field.
 * Every token here IS overridden under `.dark` in src/styles/index.css —
 * checked pair by pair, because the ones that are not (`surface-error`,
 * `surface-orange`, `hint-*-bg`) are exactly how a near-white panel with
 * near-white text ships to dark-mode users.
 */
const TONE_SURFACE: Record<BannerTone, string> = {
  neutral: "surface-secondary outline-quaternary font-primary",
  info: "surface-blue outline-blue font-primary",
  warning: "surface-status-amber-bg outline-status-amber font-primary",
  danger: "chip-red-bg outline-error font-primary",
};

/**
 * STRIP tint per tone — deliberately not the card's.
 *
 * A strip is chrome: it butts against the app bar, so it should read as a
 * second row OF the bar, not as a coloured band laid over the product. The
 * bar is `surface-primary` with a `border-b outline-quaternary` seam, so the
 * quiet rungs take exactly that and carry their tone in the icon alone — a
 * full-bleed `surface-blue` field with a saturated blue seam was the single
 * loudest thing on the dashboard for a notice that says "in two weeks".
 *
 * Escalation still steps visibly: chrome-white (notice) → amber field
 * (warning/urgent) → red field (critical) → filled slab (blocked). Only the
 * two that mean "act now" tint the whole row, and even they keep the
 * quaternary seam so the strip never fights the bar above it.
 */
const TONE_STRIP_SURFACE: Record<BannerTone, string> = {
  neutral: "surface-primary outline-quaternary font-primary",
  info: "surface-primary outline-quaternary font-primary",
  warning: "surface-status-amber-bg outline-quaternary font-primary",
  danger: "chip-red-bg outline-quaternary font-primary",
};

/**
 * Icon tint — deliberately NOT the border colour: `font-status-amber` (#f59e0b)
 * on `surface-status-amber-bg` (#fffbeb) is ~2:1, and `font-error` on
 * `chip-red-bg` ~2.4:1. The `-dark` / chip-foreground tokens clear 7:1 in both
 * themes.
 */
const TONE_ICON_CLASS: Record<BannerTone, string> = {
  neutral: "font-tertiary",
  info: "chip-blue-fg",
  warning: "font-status-amber-dark",
  danger: "chip-red-fg",
};

const TONE_ICON: Record<BannerTone, React.ElementType> = {
  neutral: IconInfoCircle,
  info: IconInfoCircle,
  warning: IconAlertTriangle,
  danger: IconAlertCircle,
};

/** `alert` (assertive) only for danger — an advisory that shouts reads as a failure. */
const TONE_ROLE: Record<BannerTone, "status" | "alert"> = {
  neutral: "status",
  info: "status",
  warning: "status",
  danger: "alert",
};

/**
 * The one loud treatment. `surface-error-dark` has no `.dark` override and
 * needs none — #d92d20 with `font-light-everytime` reads on both themes.
 */
const SOLID = "surface-error-dark font-light-everytime border-transparent";

/**
 * In-flow advisory strip / callout.
 *
 * Nothing here positions. An earlier version defaulted to
 * `fixed top-0 left-0 right-0 z-40`, which lost the paint-order tie to the
 * sticky app bar (also z-40, later in the DOM) — the title was simply painted
 * over — and spanned underneath the z-10000 Velo rail, taking its own dismiss
 * button with it. The caller's mount point decides placement now; keep these
 * in normal flow so they push content instead of covering it.
 */
export const PBanner: React.FC<PBannerProps> = ({
  tone,
  shape = "strip",
  emphasis = "quiet",
  title,
  description,
  descriptionOnNarrow = false,
  onDismiss,
  actionHref,
  actionLabel,
  actionSlot,
  iconSlot,
  testId,
  rung,
}) => {
  const t = useTranslations("banner");
  const solid = emphasis === "solid";
  const Icon = TONE_ICON[tone];
  const strip = shape === "strip";

  return (
    // `@container` and not `sm:` — the strip's own width is not the viewport:
    // inside the event layout it shares its row with the docked sidebar, and
    // the Velo drawer takes another 340px off it via `--ai-rail-push`. A
    // viewport-keyed breakpoint would keep the action cluster on one line in a
    // 380px column, which is where it has no room.
    <div
      data-testid={testId ?? "pbanner"}
      data-tone={tone}
      data-rung={rung}
      className={`no-print @container vlt-strip-in w-full ${
        strip ? "border-b" : "border rounded-lg"
      } ${solid ? SOLID : strip ? TONE_STRIP_SURFACE[tone] : TONE_SURFACE[tone]}`}
    >
      <div
        role={TONE_ROLE[tone]}
        className={`flex flex-wrap gap-x-3 gap-y-1.5 ${
          strip
            ? // Byte-identical gutters to AppBarContent's row, so the icon sits
              // under the logo and the action cluster lines up with the bar's
              // own right-hand icons instead of floating a few pixels off them.
              // (Pinned in PBanner.positioning.test.tsx by reading the bar's
              // source — do not restate the literal in prose here, that alone
              // used to satisfy the guard.)
              "items-center py-2 px-3 lg:pl-5 lg:pr-4"
            : "items-center px-3 py-2"
        }`}
      >
        {/* `basis-full` below @md forces the text to own the whole row so the
            action cluster wraps beneath it; at @md it becomes a grow-from-zero
            item and everything sits on one line. Written as basis/grow rather
            than `flex-1 basis-full` — those are different Tailwind v4 utility
            groups and which one wins depends on emitted stylesheet order. */}
        <div
          className={`flex gap-2 min-w-0 basis-full @md:basis-0 @md:grow ${
            strip ? "items-center" : "items-start"
          }`}
        >
          {iconSlot !== undefined ? (
            <span className={`shrink-0 inline-flex ${strip ? "" : "mt-0.5"}`}>{iconSlot}</span>
          ) : (
            <Icon
              size={16}
              aria-hidden
              className={`shrink-0 ${strip ? "" : "mt-0.5"} ${
                solid ? "font-light-everytime" : TONE_ICON_CLASS[tone]
              }`}
            />
          )}
          {/* One paragraph, not a title stacked over a description: the point
              is a single quiet line that only grows when the text needs it.
              The description is hidden on narrow containers so a phone gets
              the (already short, already localised) headline alone.

              A STRIP stays exactly one line (`truncate`): its height is chrome
              geometry, and a locale whose sentence is 60 characters longer than
              the German one must not turn the bar into a two-line paragraph
              block. The full text stays reachable as the native tooltip, and
              the whole point of the row — the action next to it — never moves.
              A CARD wraps, because there the text IS the content. */}
          <p
            className={`body-2-regular min-w-0 ${strip ? "truncate" : ""}`}
            title={strip && description ? `${title} — ${description}` : undefined}
          >
            <span className="body-2-medium">{title}</span>
            {description && (
              <span
                className={`${descriptionOnNarrow && !strip ? "inline" : "hidden @lg:inline"} ${solid ? "opacity-90" : "font-tertiary"}`}
              >
                {" "}
                {description}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {actionSlot ??
            (actionHref && actionLabel ? (
              // A bordered pill, not an underlined link: the house grammar for
              // "this row has one thing to do" (DESIGN_NOTES — rounded-lg,
              // border on resting chrome, hover + focus-visible on everything).
              // The underlined variant read as raw browser default sitting in
              // the middle of a coloured field.
              <Link
                to={actionHref}
                className={`body-2-medium whitespace-nowrap rounded-lg border px-2.5 py-1 transition-colors motion-reduce:transition-none focus-ring ${
                  solid
                    ? "surface-primary font-primary border-transparent hover:opacity-90"
                    : "surface-primary outline-quaternary hover:surface-secondary"
                }`}
              >
                {actionLabel}
              </Link>
            ) : null)}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t("dismiss")}
              // Hover fill only on the untinted rungs: `surface-secondary` on
              // top of the amber/red field is a grey smudge, so those keep the
              // colour-only hover.
              className={`shrink-0 rounded-md p-1.5 cursor-pointer transition-colors motion-reduce:transition-none focus-ring ${
                solid
                  ? "hover:opacity-70"
                  : `font-tertiary hover:font-primary ${
                      tone === "neutral" || tone === "info" ? "hover:surface-secondary" : ""
                    }`
              }`}
            >
              <IconX size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
